import { useTranslation } from 'react-i18next';

import { TranscriptionLanguage } from '@treams/treams-network';
import { Flag } from '@treams/treams-ui/common/Flag';
import { Select } from '@treams/treams-ui/common/Select';

type FlagLanguageID =
	| 'nl_NL'
	| 'en_US'
	| 'es_ES'
	| 'fr_FR'
	| 'de_DE'
	| 'it_IT'
	| 'pt_BR'
	| 'da_DK'
	| 'sv_SE'
	| 'pl_PL';

const LANGUAGE_CONFIG: Record<TranscriptionLanguage, { flagID: FlagLanguageID; labelKey: string }> =
	{
		[TranscriptionLanguage.Nl]: { flagID: 'nl_NL', labelKey: 'common.language.nl_NL' },
		[TranscriptionLanguage.En]: { flagID: 'en_US', labelKey: 'common.language.en_US' },
		[TranscriptionLanguage.De]: { flagID: 'de_DE', labelKey: 'common.language.de_DE' },
		[TranscriptionLanguage.Fr]: { flagID: 'fr_FR', labelKey: 'common.language.fr_FR' },
		[TranscriptionLanguage.Es]: { flagID: 'es_ES', labelKey: 'common.language.es_ES' },
		[TranscriptionLanguage.It]: { flagID: 'it_IT', labelKey: 'common.language.it_IT' },
		[TranscriptionLanguage.Pt]: { flagID: 'pt_BR', labelKey: 'common.language.pt_BR' },
		[TranscriptionLanguage.Da]: { flagID: 'da_DK', labelKey: 'common.language.da_DK' },
		[TranscriptionLanguage.Sv]: { flagID: 'sv_SE', labelKey: 'common.language.sv_SE' },
		[TranscriptionLanguage.Pl]: { flagID: 'pl_PL', labelKey: 'common.language.pl_PL' },
	};

const LANGUAGES = Object.values(TranscriptionLanguage);

const LOCALE_TO_TRANSCRIPTION_LANGUAGE: Record<string, TranscriptionLanguage> = {
	nl_NL: TranscriptionLanguage.Nl,
	en_US: TranscriptionLanguage.En,
	de_DE: TranscriptionLanguage.De,
	fr_FR: TranscriptionLanguage.Fr,
	it_IT: TranscriptionLanguage.It,
	es_ES: TranscriptionLanguage.Es,
	pt_BR: TranscriptionLanguage.Pt,
	da_DK: TranscriptionLanguage.Da,
	sv_SE: TranscriptionLanguage.Sv,
	pl_PL: TranscriptionLanguage.Pl,
};

export function localeToTranscriptionLanguage(locale: string): TranscriptionLanguage {
	return LOCALE_TO_TRANSCRIPTION_LANGUAGE[locale] ?? TranscriptionLanguage.En;
}

/**
 * The flag shown for a transcription language, for read-only displays.
 * @param language The language a transcription is recorded in.
 */
export function transcriptionLanguageFlagID(language: TranscriptionLanguage): FlagLanguageID {
	return LANGUAGE_CONFIG[language].flagID;
}

/**
 * The translation key of a transcription language's name.
 * @param language The language a transcription is recorded in.
 */
export function transcriptionLanguageLabelKey(language: TranscriptionLanguage): string {
	return LANGUAGE_CONFIG[language].labelKey;
}

export function TranscriptionLanguageSelect(props: {
	value: TranscriptionLanguage;
	onChange: (value: TranscriptionLanguage) => void;
	compact?: boolean;
	small?: boolean;
	'aria-label'?: string;
}) {
	const { t } = useTranslation();

	const selected = LANGUAGE_CONFIG[props.value];

	return (
		<Select
			small={props.small}
			tint={false}
			search={false}
			role="combobox"
			aria-label={props['aria-label']}
			selectedValue={props.value}
			selectedOption={{
				icon: <Flag languageID={selected.flagID} />,
				title: props.compact ? '' : t(selected.labelKey),
				value: props.value,
			}}
			options={LANGUAGES.map((lang) => {
				const config = LANGUAGE_CONFIG[lang];
				return {
					icon: <Flag languageID={config.flagID} />,
					title: t(config.labelKey),
					value: lang,
				};
			})}
			onChange={(value) => {
				props.onChange(value as TranscriptionLanguage);
			}}
		/>
	);
}
